/* @flow */


import JSONP from '../jsonp';
import bidbase from './bidbase';
import adconfig from '../adconfig';
import { logError } from '../utils/logger';
import { allSettledPromise } from '../utils/promiseutils';
import { getLocationHref } from '../utils/browserutil';

declare type anxbidResp = {
  cpm: number,
  ad: string
};

declare type anxbidRespWrap = {
  result: anxbidResp
};
/**
 * @module bidders/breal
 * @requires module:logger
 * @requires module:promiseutils
 */
/**
 * @global
 * @typedef {object} brealConfig
 * @description extends {@link hbConfig}
 * @property {string} bidUrl
 */

/**
 * @typedef {object} hbPosData
 */

/**
 *  @typedef hbPosData
 * @property {string} info
 * @property {object} _QP
 * @property {object} _CV
 * @property {hb_data} winningBid
 */

/**
 *  @typedef hb_data
 * @property {string} hb_pb
 * @property {string} hb_bidder
 * @property {string} hb_adid
 */

/**
 *  @typedef syncObj
 * @property {string} name
 * @property {string} type
 * @property {string} url
 */

/**
 * @description Implements functionality for breal header bidding
 * @augments _bidbase
 * @requires module:utils
 * @requires module:jsonp
 */
class _brealbid extends bidbase {
  _config: brealConfig;

  /**
   * Create brealbid obj
   *
   * @param {string} name - Partner's name
   */
  constructor(name: string) {
    super(name);
    this._mandatoryConfigs = ['bidUrl'];
    this._renderType = 'iframe';
  }

  /**
   * Process bid response. Response Format:
   * _bidResponses[id].cpm - ecpm
   * _bidResponses[id].ad - ad ifrmae url
   *
   * @param {Array<string>} posList - array of positions
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    let id = '';
    posList.forEach(pos => {
      id = this.getPosKey(pos);
      const res: anxbidResp = this._bidResponses[id];
      if (res && res.cpm > 0 && res.ad) {
        this._handleBidPosData(ret, pos, `${res.cpm / 10000}`, id, res.ad);
      }
    });
    return ret;
  }
  _getPosSizeForURL(pos: string): string {
    const { width = 0, height = 0 } = adconfig.getPosSettings(pos) || {};
    return `${width}x${height}`;
  }
  _getBid(pos: string): Promise<boolean> {
    const ccpa = this._getPrivacyString();
    // build params for bid request
    const params = {};
    /* eslint-disable unicorn/no-immediate-mutation */
    // $FlowFixMe
    params.data = {
      id: this.getPosKey(pos),
      size: this._getPosSizeForURL(pos),
      ...ccpa,
      referrer: getLocationHref()
    };
    /* eslint-enable unicorn/no-immediate-mutation */
    params.logPrefix = pos;
    params.timeout = this._config.timeout;
    return JSONP(this._config.bidUrl, params).then((data: anxbidRespWrap) => {
      if (data && data.result) {
        this._bidResponses[this.getPosKey(pos)] = data.result;
      }
      return true;
    });
  }

  /**
   * This method will be called for bidders with overriden userSync method
   *
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    const ret = [];
    if (this._config.syncUrl.length) {
      ret.push({
        name: this.getName(),
        type: 'iframe',
        url: this._config.syncUrl
      });
    }
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};

    // Create promise array with all bid requests
    const pArray = posList.map(pos => this._getBid(pos));

    // Resolve all accumulated promise here
    return allSettledPromise(pArray).then(
      arr => {
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);
        if (fValues.length) {
          // Log all failed promise
          logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      },
      (error: string) => {
        logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      }
    );
  }
}

// add bidders to bidmanager
const breal: _brealbid = new _brealbid('breal');
const appnexus: _brealbid = new _brealbid('appnexus');
export { breal, appnexus };
